import React from 'react';
import { useTranslation } from '../../i18n';
import { useAiQuota } from '../../hooks/useAiQuota';
import { AI_QUOTA_POOLS, AiQuota } from '../../services/aiQuotaService';
import { formatTime } from '../../utils/format';
import Icon from '../Icon';
import './AiQuota.css';

const resetTime = (quota: AiQuota, language: string) => formatTime(quota.resets_at, language);

// An unexpected answer (old server, proxy error page) must not take the page down with it
const isUsable = (quota: AiQuota | null | undefined): quota is AiQuota =>
  Boolean(quota?.pools) && AI_QUOTA_POOLS.every((pool) => quota?.pools[pool]);

/** Settings page: today's AI quota per pool with a bar (Faz 10). */
export const AiQuotaSection: React.FC = () => {
  const { t, language } = useTranslation();
  const { quota } = useAiQuota();

  return (
    <div className="profile-section ai-quota">
      <h2><Icon name="sparkles" /> {t('aiQuota.title')}</h2>
      {!isUsable(quota) ? (
        <p className="ai-quota__hint">{t('common.loading')}</p>
      ) : (
        <>
          <p className="ai-quota__hint">{t('aiQuota.resets', { time: resetTime(quota, language) })}</p>
          <ul className="ai-quota__list">
            {AI_QUOTA_POOLS.map((pool) => {
              const { used, limit, remaining } = quota.pools[pool];
              const percent = limit ? Math.round((used / limit) * 100) : 0;
              return (
                <li key={pool} className={`ai-quota__row ${remaining === 0 ? 'is-empty' : ''}`}>
                  <div className="ai-quota__row-head">
                    <span className="ai-quota__label">{t(`aiQuota.pools.${pool}`)}</span>
                    <span className="ai-quota__count">{t('aiQuota.remaining', { remaining, limit })}</span>
                  </div>
                  <div
                    className="ai-quota__bar"
                    role="progressbar"
                    aria-label={t(`aiQuota.pools.${pool}`)}
                    aria-valuemin={0}
                    aria-valuemax={limit}
                    aria-valuenow={used}
                  >
                    <span style={{ width: `${percent}%` }} />
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
};

/** Home screen: a one-line summary of what is left today; hidden until loaded. */
export const AiQuotaSummary: React.FC = () => {
  const { t, language } = useTranslation();
  const { quota } = useAiQuota();
  if (!isUsable(quota)) return null;

  return (
    <div className="ai-quota-summary" aria-label={t('aiQuota.summaryTitle')}>
      <span className="ai-quota-summary__title"><Icon name="sparkles" /> {t('aiQuota.summaryTitle')}</span>
      {AI_QUOTA_POOLS.map((pool) => {
        const { remaining, limit } = quota.pools[pool];
        return (
          <span key={pool} className={`ai-quota-summary__chip ${remaining === 0 ? 'is-empty' : ''}`}>
            {t(`aiQuota.short.${pool}`)} <strong>{remaining}/{limit}</strong>
          </span>
        );
      })}
      <span className="ai-quota-summary__reset">{t('aiQuota.resetsShort', { time: resetTime(quota, language) })}</span>
    </div>
  );
};
